import { toast } from "@gigpro/ui/Toast";
import type { JobPostingResponse, WorkedWithYouPro } from "@sidegigllc/api/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useInviteProsToJob } from "api/queries/hire";
import { JobLiveStep } from "domains/hire/components/JobLiveStep";
import { JobsTeaserCtaContainer } from "domains/hire/containers/JobsTeaserCta";
// oxlint-disable-next-line import/no-cycle -- invite, new-job, live and send-offer dialogs chain into each other by design
import { SendHireOfferDialogContainer } from "domains/hire/containers/SendHireOfferDialog";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { isJobsLocked } from "domains/hire/jobsPlan";
import React from "react";
import { track } from "utils/analytics";
import { captureException } from "utils/errorReporting";
import { toastApiError } from "utils/toastApiError";
import { workedWithYouQuery } from "./queries";

const MAX_SHOWN_PROS = 3;

interface JobLiveStepContainerProps {
  locationId: number;
  job: JobPostingResponse;
  onDone: () => void;
}

/** Shown in place of the posting form once a new job is saved, when `hire-offer-moments` is on. */
export const JobLiveStepContainer: React.FC<JobLiveStepContainerProps> = ({ locationId, job, onDone }) => {
  const { status, isStaff } = useJobsGateState(locationId);
  const query = workedWithYouQuery({ jobId: job.id });
  const { data, isPending } = useQuery(query);
  const queryClient = useQueryClient();
  const inviteMutation = useInviteProsToJob();
  const [offering, setOffering] = React.useState<WorkedWithYouPro>();
  const [offerDialogOpen, setOfferDialogOpen] = React.useState(false);
  const [offeredProIds, setOfferedProIds] = React.useState<ReadonlySet<number>>(new Set());
  const [inviteChecked, setInviteChecked] = React.useState(true);

  const pros = React.useMemo(() => (data?.results ?? []).filter((pro) => !pro.dismissed), [data]);
  const shownPros = pros.slice(0, MAX_SHOWN_PROS);
  // Offered or already-applied pros need no invite
  const invitablePros = pros.filter((pro) => !pro.applied && !pro.has_offer && !offeredProIds.has(pro.pro.id));
  // Without a Jobs plan the invite would reach Pros, so the plan card takes its place.
  const gated = isJobsLocked(status, isStaff);
  const canInvite = !gated && invitablePros.length > 0;

  const hasShownPros = shownPros.length > 0;
  const shownCount = shownPros.length;
  // An effect event reads the latest count without re-emitting Shown when a refetch changes the list
  const emitShown = React.useEffectEvent(() => {
    track("Hire Job Live Step Shown", {
      job_posting_id: job.id,
      pro_count: shownCount,
      invite_offered: canInvite,
    });
  });
  React.useEffect(() => {
    if (hasShownPros) emitShown();
  }, [hasShownPros]);

  const handleOffer = (pro: WorkedWithYouPro) => {
    track("Hire Job Live Step Offer Clicked", { job_posting_id: job.id, pro_id: pro.pro.id });
    setOffering(pro);
    setOfferDialogOpen(true);
  };

  const handleDone = async () => {
    if (inviteChecked && canInvite) {
      try {
        const { failed } = await inviteMutation.mutateAsync({
          jobId: job.id,
          proIds: invitablePros.map((pro) => pro.pro.id),
        });
        track("Hire Job Live Step Invites Sent", {
          job_posting_id: job.id,
          invited_count: invitablePros.length - failed,
          failed_count: failed,
        });
        if (failed > 0) toast.error(`${failed} invite${failed === 1 ? "" : "s"} didn't send.`);
      } catch (error) {
        toastApiError(error);
        captureException(error);
      }
    }
    onDone();
  };

  return (
    <>
      <JobLiveStep
        jobName={job.name}
        pros={shownPros}
        offeredProIds={offeredProIds}
        canInvite={canInvite}
        inviteChecked={inviteChecked}
        onInviteCheckedChange={setInviteChecked}
        onOffer={handleOffer}
        onDone={() => void handleDone()}
        isSubmitting={inviteMutation.isPending || isPending}
        trialCard={gated ? <JobsTeaserCtaContainer locationId={locationId} onJobPage jobApplicants={0} /> : undefined}
      />
      {offering && (
        <SendHireOfferDialogContainer
          open={offerDialogOpen}
          onClose={() => setOfferDialogOpen(false)}
          proId={offering.pro.id}
          jobId={job.id}
          onSuccess={() => {
            setOfferedProIds((ids) => new Set(ids).add(offering.pro.id));
            void queryClient.invalidateQueries({ queryKey: query.queryKey });
          }}
        />
      )}
    </>
  );
};
